<script setup lang="ts">
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import { injectMandatory, useHttpClient, useLoadingState } from "@/vf"
import type { AxiosError } from "axios"
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import { TaskViewMode } from "../../Issue/util"
import type {
    ChampionshipRegistrationFinalizeTeamData,
    ChampionshipRegistrationTeamMember,
} from "../championship-registration"
import ChampionshipRegistrationEditTeam from "./ChampionshipRegistrationEditTeam.vue"

const props = defineProps<{
    team: ChampionshipRegistrationTeam
    data: ChampionshipRegistrationFinalizeTeamData
    action: "changeRequest" | "finalize"
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const members = ref<ChampionshipRegistrationTeamMember[]>(
    props.team.members!.map(member => ({
        memberId: member.memberId!,
        type: member.type! as ChampionshipRegistrationTeamMember["type"],
    })),
)

const loading = useLoadingState()
const { t } = useI18n()
const errors = ref<string[]>([])

async function saveFinalize() {
    await http.put(
        `/championship/registration/${props.data.registrationId}/finalize-teams/${props.team.id}/members?viewMode=${viewMode}`,
        {
            members: members.value,
            categoryId: props.team.championshipCategory!.id,
            teamName: props.team.teamName,
        },
    )
    emit("updated")
}

async function saveChangeRequest() {
    try {
        await http.post(
            `/championship/registration/${props.data.registrationId}/change-request/${props.team.id}/members`,
            {
                members: members.value,
            },
            {
                params: {
                    viewMode,
                },
            },
        )
        emit("updated")
    } catch (e) {
        const err = e as AxiosError<"No changes performed" | { blockers: string[] }>

        if (err.response?.data === "No changes performed") {
            emit("updated")
            return
        }

        if (err.response?.data.blockers) {
            errors.value = err.response.data.blockers.map(blocker => t(`@championship:registration.blocker.${blocker}`))
            return
        }

        throw e
    }
}

async function save() {
    loading.while(async () => {
        switch (props.action) {
            case "finalize":
                await saveFinalize()
                break
            case "changeRequest":
                await saveChangeRequest()
                break
        }
    })
}
</script>
<template>
    <div v-if="errors.length" class="alert alert-danger">
        <p v-for="(error, index) in errors" :key="index">{{ error }}</p>
    </div>

    <ChampionshipRegistrationEditTeam
        :category-id="team.championshipCategory!.id!"
        :submitting="loading.state"
        :team="team"
        v-model="members"
        @save="save"
    >
        <template v-if="action === 'changeRequest'" #submitLabel>
            {{ t("@tasks:tasks.championship_registration.request_changes.submit") }}
        </template>
    </ChampionshipRegistrationEditTeam>
</template>
